import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { Person, Brightness4, Brightness7, Add, LocalBar, Home, SquareFoot, Scale, Kitchen } from '@mui/icons-material';
import * as Icons from '@mui/icons-material';
import { useThemePreferences } from '../contexts/ThemeContext.js';
import { useUser } from '../contexts/UserContext.js';

const TopBar = () => {
  const { darkMode, unitPreference, toggleDarkMode, toggleUnitPreference } = useThemePreferences();
  const { user, handleLogout } = useUser();
  const [anchorEl, setAnchorEl] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDarkModeToggle = () => {
    toggleDarkMode();
    handleMenuClose();
  };

  const handleUnitPreferenceToggle = () => {
    toggleUnitPreference();
    handleMenuClose();
  };

  const handleLogoutClick = () => {
    handleLogout();
    handleMenuClose();
  };

  // If mobile, do not render the top bar.
  if (isMobile) {
    return null;
  }

  return (
    <AppBar
      position="sticky"
      color="default"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: theme.palette.background.paper,
        py: 0.5
      }}
    >
      <Container>
        <Toolbar
          disableGutters
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '44px'
          }}
        >
          {/* Left: Trove */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  fontFamily: 'Montserrat, Roboto, sans-serif',
                  cursor: 'pointer'
                }}
              >
                Trove
              </Typography>
            </Link>
          </Box>

          {/* Center: Navigation links - updated to new simplified navigation */}
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button color="inherit" startIcon={<Home />}>Home</Button>
              </Link>
              <Link to="/recipe/new" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button color="inherit" startIcon={<Add />}>Add Recipe</Button>
              </Link>
              <Link to="/ingredients" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button color="inherit" startIcon={<Icons.Kitchen />}>Ingredients</Button>
              </Link>
              <Link to="/cocktail-menus" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button color="inherit" startIcon={<LocalBar />}>My Cocktail Menus</Button>
              </Link>
            </Box>
          </Box>

          {/* Right: Authentication */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {user ? (
              <>
                <IconButton onClick={handleMenuOpen} sx={{ ml: 2 }}>
                  <Avatar src={user.photoURL} alt={user.displayName}>
                    {user.displayName?.[0] || <Person />}
                  </Avatar>
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  <MenuItem onClick={handleDarkModeToggle}>
                    <ListItemIcon>
                      {darkMode ? <Brightness7 /> : <Brightness4 />}
                    </ListItemIcon>
                    <ListItemText>
                      {darkMode ? 'Light Mode' : 'Dark Mode'}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleUnitPreferenceToggle}>
                    <ListItemIcon>
                      {unitPreference === 'imperial' ? <Scale /> : <SquareFoot />}
                    </ListItemIcon>
                    <ListItemText>
                      {unitPreference === 'imperial' ? 'Metric Units (ml)' : 'Imperial Units (oz)'}
                    </ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogoutClick}>
                    <ListItemText>Sign Out</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button component={Link} to="/login" startIcon={<Person />} color="primary">
                Sign In
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default TopBar;
